let CURRENT_PROJECT_ID = '';

export const setCurrentProjectId = (id) => {
  CURRENT_PROJECT_ID = id || '';
};

const projectState = new Map();
const cssLoadPromises = new Map();

const SYSTEM_FONTS = new Set(
  [
    'Arial',
    'Arial Black',
    'Times New Roman',
    'Times',
    'Helvetica',
    'Helvetica Neue',
    'Courier New',
    'Courier',
    'Georgia',
    'Verdana',
    'Tahoma',
    'Trebuchet MS',
    'Calibri',
    'Cambria',
    'Garamond',
    'Palatino',
    'Lucida Console',
    'Lucida Sans Unicode',
    'Segoe UI',
    'Segoe UI Emoji',
    'Segoe UI Symbol',
    'Aptos',
    'Aptos Display',
    'Aptos Narrow'
  ].map((s) => s.toLowerCase())
);

function isSystemFont(family) {
  return SYSTEM_FONTS.has(String(family || '').toLowerCase());
}

function getState(projectId) {
  const pid = projectId || CURRENT_PROJECT_ID || '__no_project__';
  if (!projectState.has(pid)) {
    projectState.set(pid, {
      needByFamily: new Map(),
      loadedByFamily: new Map(),
      missingFamilies: new Set()
    });
  }
  return projectState.get(pid);
}

function normalizeFontName(name) {
  if (!name) return null;
  return String(name)
    .replace(/\s*\(.*?\)\s*/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function variantKey(weight, style) {
  const w = weight === 700 ? 700 : 400;
  const s = style === 'italic' ? 'italic' : 'normal';
  return `${w}:${s}`;
}

function styleToVariant(styleObj) {
  const bold = !!styleObj?.bold;
  const italic = !!styleObj?.italic;
  return variantKey(bold ? 700 : 400, italic ? 'italic' : 'normal');
}

function injectCssOnce(href) {
  if (cssLoadPromises.has(href)) return cssLoadPromises.get(href);
  const p = new Promise((resolve, reject) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.onload = () => resolve();
    link.onerror = () => reject(new Error(`Failed to load font CSS: ${href}`));
    document.head.appendChild(link);
  });
  cssLoadPromises.set(href, p);
  return p;
}

function buildGoogleFamilyParam(family, variantsSet) {
  const variants = [...variantsSet].map((v) => {
    const [wStr, s] = String(v).split(':');
    return { w: Number(wStr) || 400, italic: s === 'italic' };
  });
  const hasItalic = variants.some((x) => x.italic);
  const weights = Array.from(new Set(variants.map((x) => x.w))).sort((a, b) => a - b);
  const famEnc = encodeURIComponent(family);
  if (!hasItalic) {
    if (weights.length <= 1) return `family=${famEnc}`;
    return `family=${famEnc}:wght@${weights.join(';')}`;
  }
  const tuples = [];
  for (const w of weights) {
    tuples.push(`0,${w}`);
    tuples.push(`1,${w}`);
  }
  return `family=${famEnc}:ital,wght@${tuples.join(';')}`;
}

async function waitForFontAvailability(family, variantsSet) {
  if (!document.fonts?.load) return true;
  const checks = [];
  for (const v of variantsSet) {
    const [wStr, s] = v.split(':');
    const w = Number(wStr) || 400;
    checks.push(document.fonts.load(`${s} ${w} 16px "${family}"`));
  }
  const results = await Promise.allSettled(checks);
  return results.some(
    (r) => r.status === 'fulfilled' && Array.isArray(r.value) && r.value.length > 0
  );
}

async function ensureFamilyLoadedGoogle(projectId, family) {
  const st = getState(projectId);
  if (!family) return;
  if (isSystemFont(family)) {
    st.loadedByFamily.set(family, st.needByFamily.get(family) || new Set());
    return;
  }
  if (st.missingFamilies.has(family)) return;
  const need = st.needByFamily.get(family);
  if (!need || need.size === 0) return;
  const already = st.loadedByFamily.get(family) || new Set();
  let hasNew = false;
  for (const v of need) {
    if (!already.has(v)) {
      hasNew = true;
      break;
    }
  }
  if (!hasNew) return;
  const union = new Set([...already, ...need]);
  const famParam = buildGoogleFamilyParam(family, union);
  const href = `https://fonts.googleapis.com/css2?${famParam}&display=swap`;
  try {
    await injectCssOnce(href);
  } catch (e) {
    st.missingFamilies.add(family);
    return;
  }
  const ok = await waitForFontAvailability(family, union);
  if (!ok) {
    st.missingFamilies.add(family);
    return;
  }
  st.loadedByFamily.set(family, union);
}

function collectFontsFromSlides(pptJson, maxSlides) {
  const slides = pptJson?.slides || [];
  const n =
    typeof maxSlides === 'number' ? Math.min(Math.max(maxSlides, 0), slides.length) : slides.length;
  const need = new Map();
  const add = (familyRaw, styleObj) => {
    const family = normalizeFontName(familyRaw);
    if (!family) return;
    const v = styleToVariant(styleObj);
    if (!need.has(family)) need.set(family, new Set());
    need.get(family).add(v);
  };
  for (let i = 0; i < n; i++) {
    const slide = slides[i];
    if (!slide || slide.isSkeleton) continue;
    for (const shape of slide?.shapes || []) {
      const tb = shape?.text;
      if (!tb) continue;
      for (const p of tb.paragraphs || []) {
        if (p?.listStyle?.font) add(p.listStyle.font, p.listStyle);
        for (const r of p?.runs || []) {
          add(r?.style?.font, r?.style || {});
        }
      }
    }
  }
  return need;
}

export async function preloadProjectFontsForProject(projectId, pptJson, opts = {}) {
  if (!projectId) return;
  const st = getState(projectId);
  const needNow = collectFontsFromSlides(pptJson, opts.maxSlides);
  for (const [family, variants] of needNow.entries()) {
    if (st.missingFamilies.has(family)) continue;
    if (!st.needByFamily.has(family)) st.needByFamily.set(family, new Set());
    const cur = st.needByFamily.get(family);
    for (const v of variants) cur.add(v);
  }
  const families = Array.from(st.needByFamily.keys());
  await Promise.allSettled(families.map((fam) => ensureFamilyLoadedGoogle(projectId, fam)));
}

export async function preloadProjectFonts(pptJson, opts = {}) {
  const pid = CURRENT_PROJECT_ID;
  if (!pid) return;
  return preloadProjectFontsForProject(pid, pptJson, opts);
}
